import { IconAI } from "@/assets/icons/geist/IconAI";
import { IconArrowDown } from "@/assets/icons/geist/IconArrowDown";
import { IconClose } from "@/assets/icons/geist/IconClose";
import { IconCode } from "@/assets/icons/geist/IconCode";
import {
    InputWithCodeContext,
    type AgentContext,
    type CodeContext,
    type InputWithCodeContextImperativeHandle,
} from "@/components/ai/input-with-code-context/InputWithCodeContext";
import { Button } from "@/components/flexkit/Button";
import { KeyboardKey } from "@/components/KeyboardKey";
import { cn } from "@/components/podkit/lib/cn";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Text } from "@/components/podkit/typography/Text";
import { Tooltip } from "@/components/Tooltip";
import { getBranch } from "@/hooks/use-branch";
import { useEnvironmentsByProjects } from "@/hooks/use-environments-by-projects";
import { useListAgents, useSendToAgentExecution, type PlainAgent } from "@/queries/agent-queries";
import { useCreateAgentExecution } from "@/queries/agent-queries";
import { useListProjects } from "@/queries/project-queries";
import { getAgentExecutionUrl } from "@/routes/ai/ai-urls";
import { formatError } from "@/utils/errors";
import { getPlatformModifierKey } from "@/utils/platform";
import {
    AgentCodeContext,
    SendToAgentExecutionRequest,
    UserInputBlock,
    UserInputBlock_TextInput,
} from "gitpod-next-api/gitpod/v1/agent_pb";
import { useCallback, useMemo, useRef, useState, type FC, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";

export const PromptInput: FC<{ defaultAgentId?: string }> = ({ defaultAgentId }) => {
    const imperativeHandleRef = useRef<InputWithCodeContextImperativeHandle>(null);

    const [prompt, setPrompt] = useState("");

    const [codeContext, setCodeContext] = useState<CodeContext | undefined>(undefined);
    const [agentId, setAgentId] = useState<string | undefined>(defaultAgentId);

    const { data: agents } = useListAgents();
    const { data: projects } = useListProjects();
    const { data: environmentGroups } = useEnvironmentsByProjects({
        // We want to include agent-created environments in the dropdown
        includeAgentCreatedEnvironments: true,
    });

    const formRef = useRef<HTMLFormElement>(null);

    const { toast } = useToast();
    const navigate = useNavigate();

    const startAgentExecution = useCreateAgentExecution();
    const sendToAgentExecution = useSendToAgentExecution();

    const onSubmit = useCallback(
        async (prompt: string, codeContext?: CodeContext) => {
            if (!agentId) {
                return;
            }
            let agentCodeContext: AgentCodeContext | undefined = undefined;
            try {
                if (codeContext?.type === "project") {
                    agentCodeContext = new AgentCodeContext({
                        context: {
                            case: "projectId",
                            value: codeContext.value.id,
                        },
                    });
                } else if (codeContext?.type === "environment") {
                    agentCodeContext = new AgentCodeContext({
                        context: {
                            case: "environmentId",
                            value: codeContext.value.id,
                        },
                    });
                }

                const { agentExecutionId } = await startAgentExecution.mutateAsync({
                    agentId: agentId,
                    codeContext: agentCodeContext,
                });
                await sendToAgentExecution.mutateAsync(
                    new SendToAgentExecutionRequest({
                        agentExecutionId: agentExecutionId,
                        userInput: new UserInputBlock({
                            input: {
                                case: "text",
                                value: new UserInputBlock_TextInput({
                                    content: prompt,
                                }),
                            },
                        }),
                    }),
                );
                navigate(getAgentExecutionUrl(agentExecutionId));
            } catch (error) {
                toast({
                    title: "Failed to start agent execution",
                    description: formatError(error),
                });
            }
        },
        [agentId, startAgentExecution, sendToAgentExecution, navigate, toast],
    );

    const onAddCodeContext = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        if (imperativeHandleRef.current) {
            imperativeHandleRef.current.triggerContextCompletion();
        }
    }, []);

    const handleSubmit = useCallback(
        async (e: FormEvent) => {
            e.preventDefault();
            await onSubmit(prompt, codeContext);
        },
        [prompt, codeContext, onSubmit],
    );

    const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            e.stopPropagation();
            formRef.current?.requestSubmit();
        }
    }, []);

    const isLoading = startAgentExecution.isPending || sendToAgentExecution.isPending;
    const submitDisabled = !prompt || !agentId || isLoading;

    const hintText = useMemo(() => {
        if (codeContext && agentId) {
            return "";
        }
        if (codeContext && !agentId) {
            return "Type @ to add agent";
        }
        if (!codeContext && agentId) {
            return "Type @ to add code context";
        }
        return "Type @ to select agent and add code context";
    }, [codeContext, agentId]);

    return (
        <div className="flex w-full flex-col gap-1">
            <form
                ref={formRef}
                onSubmit={handleSubmit}
                className={cn(
                    "flex w-full flex-col gap-2 rounded-lg border border-border-base bg-surface-02 p-5 pr-[2px] has-[:focus]:border-border-yield",
                )}
            >
                <InputWithCodeContext
                    imperativeHandleRef={imperativeHandleRef}
                    className="min-h-[100px] max-w-full resize-none overflow-x-auto border-0 bg-transparent p-0 pr-14 focus:ring-0"
                    textValue={prompt}
                    setTextValue={setPrompt}
                    onCodeContextSelect={(v: CodeContext) => setCodeContext(v)}
                    onAgentContextSelect={(v: AgentContext) => setAgentId(v.value.id)}
                    placeholder="How can we help?"
                    data-testid="prompt-input-textarea"
                    maxRows={20}
                    onKeyDown={onKeyDown}
                    projects={projects?.projects ?? []}
                    environmentGroups={environmentGroups ?? []}
                    agents={agents?.agents ?? []}
                />
                <div className="flex items-center justify-between gap-2">
                    <div className="flex flex-row items-center gap-2">
                        {agentId && (
                            <AgentContextPill
                                agent={agents?.agents?.find((a) => a.id === agentId)}
                                onClear={() => setAgentId(undefined)}
                            />
                        )}
                        {codeContext && (
                            <CodeContextPill context={codeContext} onClear={() => setCodeContext(undefined)} />
                        )}
                    </div>
                    <div className="flex items-center justify-end gap-2 pr-5">
                        <Tooltip content="Add context">
                            <Button
                                variant="secondary"
                                className="m-0 size-9 p-0"
                                data-testid="prompt-input-add-code-context-button"
                                onClick={onAddCodeContext}
                            >
                                @
                            </Button>
                        </Tooltip>
                        <Tooltip
                            content={
                                <div className="flex flex-row items-center gap-2">
                                    Start conversation
                                    <div className="flex items-center gap-1">
                                        <KeyboardKey className="bg-surface-invert-tertiary">
                                            {getPlatformModifierKey()}
                                        </KeyboardKey>
                                        <KeyboardKey className="bg-surface-invert-tertiary">Enter</KeyboardKey>
                                    </div>
                                </div>
                            }
                        >
                            <Button
                                type="submit"
                                variant="primary"
                                disabled={submitDisabled}
                                loading={isLoading}
                                LeadingIcon={IconArrowDown}
                                size={"md"}
                                className="m-0 size-9 rotate-180 p-0"
                                data-testid="prompt-input-submit-button"
                            />
                        </Tooltip>
                    </div>
                </div>
            </form>
            <Text
                className={cn("min-h-[1lh] text-center text-sm text-content-tertiary opacity-0 transition-opacity", {
                    "opacity-100": !!hintText,
                })}
            >
                {hintText}
            </Text>
        </div>
    );
};

const CodeContextPill: FC<{ context?: CodeContext; onClear: () => void }> = ({ context, onClear }) => {
    const label = useMemo(() => {
        if (!context) return null;
        switch (context.type) {
            case "project":
                return context.value.metadata?.name || context.value.id;
            case "environment":
                return getBranch(context.value);
        }
    }, [context]);

    if (!context) return null;

    return (
        <div
            className={cn(
                "flex items-center gap-1 rounded-[32px] border-0.5 border-border-base py-0.5 pl-1.5 pr-2",
                "group",
                "min-w-6 bg-[rgba(144,144,129,0.1)]",
            )}
        >
            <Tooltip content="Clear code context">
                <Button
                    variant="ghost"
                    size="sm"
                    className="rounded-md border-0 p-0.5"
                    LeadingIcon={(_props) => (
                        <>
                            <IconClose
                                size="sm"
                                className="hidden size-3.5 shrink-0 text-content-tertiary group-hover:block group-focus:block"
                            />
                            <IconCode
                                size="sm"
                                className="size-3.5 shrink-0 text-content-tertiary group-hover:hidden group-focus:hidden"
                            />
                        </>
                    )}
                    onClick={onClear}
                    aria-label="Clear code context"
                />
            </Tooltip>
            <Text className="select-none truncate text-sm font-bold">{label}</Text>
        </div>
    );
};

const AgentContextPill: FC<{ agent?: PlainAgent; onClear: () => void }> = ({ agent, onClear }) => {
    const label = useMemo(() => {
        if (!agent) return null;
        return agent.metadata?.name || agent.id;
    }, [agent]);

    if (!agent) return null;

    return (
        <div
            className={cn(
                "flex items-center gap-1 rounded-[32px] border-0.5 border-border-base py-0.5 pl-1.5 pr-2",
                "group",
                "min-w-6 bg-[rgba(144,144,129,0.1)]",
            )}
        >
            <Tooltip content="Clear agent">
                <Button
                    variant="ghost"
                    size="sm"
                    className="rounded-md border-0 p-0.5"
                    LeadingIcon={(_props) => (
                        <>
                            <IconClose
                                size="sm"
                                className="hidden size-3.5 shrink-0 text-content-tertiary group-hover:block group-focus:block"
                            />
                            <IconAI
                                size="sm"
                                className="size-3.5 shrink-0 text-content-tertiary group-hover:hidden group-focus:hidden"
                            />
                        </>
                    )}
                    onClick={onClear}
                    aria-label="Clear agent"
                />
            </Tooltip>
            <Text className="select-none truncate text-sm font-bold">{label}</Text>
        </div>
    );
};
